/****************
 * The open-trades tray, beside the notification bell.
 *
 * Guillaume, 2026-09-07: "next to the notification bell there should be a icon
 * that when you click on it, it display the open trades and their performances
 * and when you click on one of them, it lead you to the session where it's
 * from."
 *
 * The bell answers "what happened"; this answers "what is happening". They are
 * different questions and the second one had no home -- to see live exposure
 * you had to open sessions one at a time and add them up yourself.
 *
 * ── What is on the list ─────────────────────────────────────────────────────
 *
 * The broker's positions, 1:1, as Guillaume asked. Not our replicated_positions
 * rows: those are an attribution, and a position the exchange holds that we
 * failed to record is precisely the one worth seeing. Where our records cannot
 * name a session the row still appears and says so.
 *
 * Where two sessions hold the same asset in the same direction, the row splits
 * by size and names each session's portion -- the one place CopyFi divides
 * anything, and the reason this endpoint exists rather than a list of our rows.
 ****************/

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CaretDown, CaretRight, ChartLineUp, Warning } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { openPnlOf } from "@/lib/openPnl";
import { useLivePrices, markPriceOf } from "@/hooks/useLivePrices";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { subscriptionApi, type OpenPositionRow, type RestingOrder } from "@/api/client";
import {
  attributionNote,
  money,
  showsSplit,
  size,
  sizesDisagree,
  toneFor,
  cancelErrorFor,
  isPaperBook,
  closedAt,
  closedAtTitle,
  closedPrices,
  liquidationReading,
} from "@/lib/openTrades";
import { useAuth } from "@/contexts/AuthContext";
import { useSessions } from "@/hooks/useSessions";
import { fmtFixed, fmtPct, fmtUsd, intlLocale, withSign } from "@/lib/format";
import { closeIntentFor } from "@/lib/closePosition";
import { ClosePositionDialog } from "@/components/ClosePositionDialog";
import { MarketClosedBadge, SymbolLabel } from '@/components/AssetType';
import { useAssetName } from '@/lib/instruments';
import { focusLink } from '@/lib/focusLink.core';
import { inSpeedRun } from '@/lib/speedRun';

// Matches the notification feed's cadence. The reconciler writes the venue book
// on its own ~15s pass, so polling faster would re-read the same row.
const POLL_MS = 30_000;

/*
 * Which broker key each row runs on (Guillaume, 10-21: "show the broker/API").
 * A session id gives its key's name; empty when it is not known yet.
 */
const KeyNameOf = createContext<(subscriptionId: number) => string>(() => "");

/** Folded or not, per section, remembered in this browser. */
function useFold(id: string): [boolean, () => void] {
  const storageKey = `copyfi.tray.fold.${id}`;
  const [folded, setFolded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setFolded((f) => {
      try {
        localStorage.setItem(storageKey, f ? "0" : "1");
      } catch {
        // Not remembered; it still folds.
      }
      return !f;
    });
  return [folded, toggle];
}

/** A section title with its count and a chevron that folds it (Guillaume, 10-21). */
function TraySection({
  id,
  title,
  count,
  children,
}: {
  id: string;
  title: string;
  count: number;
  children: ReactNode;
}) {
  const [folded, toggle] = useFold(id);
  return (
    <div className="border-t border-border first:border-t-0">
      <button
        type="button"
        aria-expanded={!folded}
        onClick={toggle}
        className="sticky top-0 z-10 flex w-full items-center gap-1.5 bg-popover px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
      >
        {folded ? <CaretRight size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
        <span>{title}</span>
        <span className="tabular-nums">({count})</span>
      </button>
      {!folded && <div className="pb-1">{children}</div>}
    </div>
  );
}

function Position({
  row,
  onOpenSession,
  onCloseRequested,
}: {
  row: OpenPositionRow;
  onOpenSession: (subscriptionId: number, symbol?: string) => void;
  onCloseRequested: (row: OpenPositionRow) => void;
}) {
  const { t } = useTranslation("trade");
  const long = row.side === "LONG";
  const liq = liquidationReading(row);
  const sessions = row.sessions ?? [];
  const single = sessions.length === 1 ? sessions[0] : undefined;
  const keyNameOf = useContext(KeyNameOf);

  // One session owns it: the whole row is the link to that session, which is
  // the common case and should not require aiming at a sub-row.
  const rowClickable = Boolean(single);

  return (
    <div
      className={`border-b border-border/60 last:border-0 px-4 py-3.5 ${
        rowClickable ? "cursor-pointer hover:bg-muted/50 transition-colors" : ""
      }`}
      onClick={single ? () => onOpenSession(single.subscription_id, row.symbol) : undefined}
      role={rowClickable ? "button" : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      onKeyDown={
        single
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenSession(single.subscription_id, row.symbol);
              }
            }
          : undefined
      }
    >
      {/****************
        * A BROKER ROW IS A LABELLED GRID, NOT A SENTENCE.
        *
        * Guillaume, 2026-09-09: "there is a massive disparity in aesthetic"
        * between how Phemex shows a position and how we did.
        *
        * He is right, and the difference is structural rather than decorative.
        * This was three stacked lines of dot-separated muted text — "0.28 @
        * 108.2 · mark 109.4", "Crossed · $48.85 margin · liq 95.10" — which is
        * PROSE. Every value had to be read in sequence to be found, nothing
        * lined up between one position and the next, and the separators carried
        * as much visual weight as the numbers.
        *
        * Every venue lands on the same answer instead: a small uppercase label
        * over a tabular value, in fixed columns. That is what lets someone with
        * four positions open compare entry against entry down the column
        * without reading a single label twice — and it is why a broker screen
        * looks calm while a list of the same facts as sentences looks busy.
        *
        * Three rules do the work here:
        *   - identity left, result right, on one line at the top. Symbol,
        *     side, leverage and mode are ONE fact (what you hold and how hard)
        *     and the PnL is the conclusion, so they sit at opposite ends.
        *   - every number tabular and monospaced, so digits align by column
        *   - colour on exactly two things: the side, and the result. Liq is the
        *     single exception, and it earns it by being a boundary rather than
        *     a measurement.
        ****************/}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <SymbolLabel symbol={row.symbol} className="font-medium text-sm truncate" />
          <MarketClosedBadge symbol={row.symbol} />
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
              long
                ? "bg-emerald-500/10 text-emerald-500"
                : "bg-red-500/10 text-red-500"
            }`}
          >
            {row.side}
          </span>
          {row.leverage > 0 && (
            <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
              {row.leverage}x
            </span>
          )}
          {row.margin_mode && (
            <span className="text-[10px] text-muted-foreground shrink-0">
              {row.margin_mode}
            </span>
          )}
        </div>

        <div className={`shrink-0 text-end ${toneFor(row.unrealized_pnl)}`}>
          <div className="text-sm font-semibold tabular-nums">
            {money(row.unrealized_pnl)}
          </div>
          {/* Return on the margin the venue is holding, not on the notional —
              which is what makes two positions of different sizes comparable
              at a glance. */}
          {Number.isFinite(row.roe) && row.roe !== 0 && (
            <div className="text-[10px] font-normal opacity-70 tabular-nums">
              {withSign(row.roe >= 0, fmtPct(row.roe, 2))}
            </div>
          )}
        </div>
      </div>

      {/* The grid. Three columns because 380px holds three comfortably and
          four crush the labels; the second row carries what the first cannot. */}
      <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1.5">
        <Field label={t("tray.field.size")} value={size(row.size)} />
        <Field label={t("tray.field.entry")} value={row.entry_price ? px4(row.entry_price) : "—"} />
        <Field label={t("tray.field.mark")} value={row.mark_price ? px4(row.mark_price) : "—"} />
        <Field
          label={t("tray.field.liq")}
          value={liq.kind === "price" ? px4(liq.price) : "—"}
          /* The only coloured value in the grid, and the only one that is a
             boundary rather than a measurement. */
          tone={liq.kind === "price" ? "text-warning" : undefined}
          /* 2026-10-01: a cross long the wallet covers in full came back from
             the venue with its smallest price step as the liquidation price
             ("0.0001"), which is no boundary. A dash, and why on hover: the
             session page's own sentences for the same case. See
             liquidationReading. */
          title={
            liq.kind === "wallet-backed"
              ? `${t("sessions:open.liqNoneTitle")} ${t("sessions:open.liqNoneCross")}`
              : undefined
          }
        />
        <Field label={t("tray.field.margin")} value={row.margin_used > 0 ? money(row.margin_used) : "—"} />
        <Field
          label={t("tray.field.notional")}
          value={
            row.entry_price && row.size
              ? money(row.entry_price * row.size)
              : "—"
          }
        />
      </div>

      {/****************
        * CLOSE, and why it is a request rather than the action itself.
        *
        * One contract can be held by more than one session, and the venue shows a single
        * merged position that cannot tell the legs apart. So this asks the tray to open the
        * dialog, which is where "whose leg" gets answered -- see ClosePositionDialog.
        *
        * stopPropagation because the row itself navigates to the session when one session
        * owns it, and the two must never be one click apart from each other.
        ****************/}
      {closeIntentFor(sessions).kind !== "none" && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCloseRequested(row);
            }}
            className="text-[11px] font-medium text-danger/90 hover:text-danger hover:underline"
          >
            {t("tray.closePosition")}
          </button>
        </div>
      )}

      {/* The single-session case needs only the trader's name; the row already
          links there, so a nested control would be a second target for the
          same action.

          Under the grid rather than between it and the header: the header is
          identity and result, the grid is the position, and whose session it
          is belongs to neither — it is a footnote to both. */}
      {single && single.trader_name && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          <span className="text-muted-foreground/70">#{single.subscription_id}</span>{" "}
          {single.trader_name}
          {keyNameOf(single.subscription_id) ? ` · ${keyNameOf(single.subscription_id)}` : ""}
        </div>
      )}

      {/* THE SHARED CASE.
          Two sessions on one asset in one direction. Named as such, with each
          session's portion, because the alternative -- showing the position
          once and silently crediting it to one session -- is the thing
          Guillaume asked us to specify. */}
      {showsSplit(row) && (
        <div className="mt-2 rounded-md bg-muted/60 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            {t("tray.sharedBy", { n: sessions.length })}
          </div>
          {/****************
            * THE FIGURES ARE NOT THE LINK. The note below is.
            *
            * Guillaume, 2026-09-09: "instead of having the Position clickable
            * to lead to a session, the note should be below and say 'This
            * Position is a combination of the Trades of session X, session Y
            * and Session Z'", with the session names as the clickable part.
            *
            * These were buttons, which put a click target on every number and
            * gave the user four places to aim at for one destination. A row of
            * figures is something to READ; the sentence underneath is what you
            * act on. Separating the two is what makes it obvious which is
            * which.
            ****************/}
          {sessions.map((s) => (
            <div
              key={s.subscription_id}
              className="w-full flex items-center justify-between gap-2 py-1 text-start"
            >
              <span className="text-xs truncate">
                {/* Always the number, name or not. `Session 254` only when the
                    trader is unknown meant the two cases read completely
                    differently, and the number is the part a user can act on —
                    it is what the URL, the logs and the support thread use. */}
                <span className="text-muted-foreground">#{s.subscription_id}</span>{" "}
                {s.trader_name || ""}
                <span className="text-muted-foreground">
                  {keyNameOf(s.subscription_id) ? ` · ${keyNameOf(s.subscription_id)}` : ""}
                  {" "}
                  · {Math.round(s.share * 100)}%
                </span>
              </span>
              <span className="shrink-0 text-end">
                <span
                  className={`text-xs font-medium ${toneFor(s.unrealized_pnl)}`}
                >
                  {money(s.unrealized_pnl)}
                </span>
                {/* What THIS session's leg has cost. The venue cannot tell two
                    sessions on one contract apart, so if this is not carried
                    per session nothing shows it at all. */}
                {(s.total_fees ?? 0) > 0 && (
                  <span className="block text-[9px] text-muted-foreground tabular-nums">
                    {/* Only the exchange's own charge; a dash while ours (2026-10-08). */}
                    {t("tray.legFees", { amount: s.fees_estimated ? "—" : money(s.total_fees) })}
                  </span>
                )}
                {Math.abs(s.funding_paid ?? 0) > 0 && (
                  <span className="block text-[9px] text-muted-foreground tabular-nums">
                    {t("tray.legFunding", { amount: money(s.funding_paid) })}
                  </span>
                )}
                {/****************
                  * WHAT THIS SESSION HAS ALREADY BANKED, AND ONLY WHEN IT HAS.
                  *
                  * G-21. An open position has two PnLs and a closed trade has
                  * one. The figure above is floating and still at risk; this is
                  * money already taken by partly closing, which cannot be lost
                  * if the rest goes against the holder.
                  *
                  * Without it a user who scaled out of half a position saw the
                  * floating figure alone — so the position read as a loss while
                  * they were up overall, and nothing on the screen said why.
                  *
                  * Never added to the figure above: a total of the two answers
                  * neither "what is at risk" nor "what have I banked". Shown
                  * only when non-zero, because a line reading $0.00 on every
                  * other row trains the reader to stop seeing it.
                  ****************/}
                {Math.abs(s.realised_so_far ?? 0) > 0 && (
                  <span className="block text-[9px] text-muted-foreground tabular-nums">
                    {t("tray.legBanked", { amount: money(s.realised_so_far as number) })}
                  </span>
                )}
              </span>
            </div>
          ))}

          {/****************
            * THE SENTENCE, AND THE ONLY LINKS IN this box.
            *
            * Each name goes to that session AND to the position it is
            * reflecting — ?open=<symbol> is the deep link that scrolls to the
            * row and rings it, so "leading to their respective session and the
            * trade they are reflecting" is one click rather than a landing
            * page and a hunt.
            *
            * Written as prose with commas and an "and", because it is a
            * sentence about what this position IS. A list of links would be
            * the same information arranged as navigation, which is what the
            * clickable rows above already were.
            ****************/}
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
            {t("tray.combination")}{" "}
            {sessions.map((s, i) => (
              <span key={s.subscription_id}>
                {i > 0 && (i === sessions.length - 1 ? t("tray.listAnd") : ", ")}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSession(s.subscription_id, row.symbol);
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  {t("tray.sessionLink", { id: s.subscription_id })}
                  {s.trader_name ? ` (${s.trader_name})` : ""}
                </button>
              </span>
            ))}
            .
          </p>

          {/* WHY THE PARTS DO NOT LOOK LIKE SLICES OF THE WHOLE.
              One position at the exchange, at a blended entry, but each
              session's profit measured from its OWN entry -- so a session can
              show +$60 while the position shows +$20 and both are right. The
              question this answers is asked the first time somebody sees two
              sessions on one contract, and it had no answer anywhere. */}
          <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
            {t("tray.blended")}
          </p>

          {/* OUR SIZES DO NOT ADD UP TO THE EXCHANGE'S.
              Inside the split box because that is what it qualifies: while this
              is true the portions above are an assertion the exchange does not
              support, and saying so beats showing a confident wrong number. */}
          {sizesDisagree(row) && (
            <div className="mt-2 flex items-start gap-1.5 rounded bg-amber-500/10 p-1.5 text-[10px] leading-relaxed text-amber-500">
              <Warning size={12} weight="fill" className="mt-px shrink-0" />
              <span>
                {t("tray.sizesDisagree", { basis: size(row.basis_size ?? 0), size: size(row.size) })}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Attribution we cannot make. Stated, never hidden: a position on the
          account that we cannot tie to a session is a fact about the account,
          and the person holding it is entitled to know it is there. */}
      {attributionNote(row) === "unmatched" && (
        <div className="flex items-center gap-1.5 text-xs text-amber-500 mt-1">
          <Warning size={12} weight="fill" />
          {t("tray.unmatched")}
        </div>
      )}

      {attributionNote(row) === "venue-missing" && (
        <div className="flex items-center gap-1.5 text-xs text-amber-500 mt-1">
          <Warning size={12} weight="fill" />
          {t("tray.venueMissing")}
        </div>
      )}

      {/* Part of the position is ours to explain and part is not. Shown only
          when the gap is real -- a rounding difference is not news. */}
      {attributionNote(row) === "partial" && (
        <div className="text-xs text-muted-foreground mt-1">
          {t("tray.partial", { attributed: size(row.attributed_size), size: size(row.size) })}
        </div>
      )}
    </div>
  );
}

/*
 * One labelled value in a position grid.
 *
 * The label is what makes the grid readable without being read: uppercase,
 * tiny and muted, it registers as a column heading rather than as content, so
 * the eye goes straight to the number underneath. Fixed here rather than
 * inlined six times so the six cannot drift apart — a grid whose columns are
 * styled slightly differently stops looking like a grid.
 */
function Field({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: string;
  tone?: string;
  title?: string;
}) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground/70">
        {label}
      </div>
      <div className={`text-xs font-mono tabular-nums truncate ${tone ?? ""}`}>
        {value}
      </div>
    </div>
  );
}

export function OpenTradesTray() {
  const an = useAssetName();
  const { t } = useTranslation("trade");
  // The recently-closed list below names each row `t`, so it reads this as tr.
  const tr = t;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Which position the close dialog is asking about. Null when it is shut.
  const [closingRow, setClosingRow] = useState<OpenPositionRow | null>(null);

  // A waiting order being cancelled, and what the broker said if it refused.
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelError, setCancelError] = useState<{ id: number; message: string } | null>(null);
  // A strategy stop has no id of ours, only the broker's (a string).
  const [cancellingStop, setCancellingStop] = useState<string | null>(null);
  const [stopCancelError, setStopCancelError] = useState<{ id: string; message: string } | null>(null);

  /*
   * LIVE OR PAPER. M-99, Guillaume 2026-09-30: "a switch for Paper and Live so
   * instead of displaying Only the Live Trading Trades from broker, we could
   * also display the Paper Trades and have the Close button there". The badge
   * on the button stays the live book: it is the one with money in it.
   */
  const [book, setBook] = useState<"live" | "paper">("live");

  /* The key each session runs on, by name: the key's label, else the
     exchange, else "API #id" (the sessions list's own rule, M-119). */
  const { data: mySessions } = useSessions(false);
  const { data: myKeys } = useQuery({
    queryKey: ["tray", "key-names"],
    queryFn: () =>
      import("@/services/brokerCredentials")
        .then((m) => m.brokerCredentialsService.getAllCredentials())
        .catch(() => [] as any[]),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  const keyNameByCredential = useMemo(() => {
    const byId = new Map<number, string>();
    for (const c of (myKeys as any[]) || []) {
      const name = c.label || c.exchange_name;
      if (name) byId.set(Number(c.id), String(name));
    }
    return byId;
  }, [myKeys]);
  const keyNameOfCredential = (credentialId: number | undefined) =>
    !credentialId ? "" : keyNameByCredential.get(Number(credentialId)) || tr("tray.keyFallback", { id: credentialId });
  const credentialOfSession = useMemo(() => {
    const bySession = new Map<number, number>();
    for (const s of (mySessions as any[]) || []) bySession.set(Number(s.id), Number(s.credential_id) || 0);
    return bySession;
  }, [mySessions]);
  const keyNameOfSession = (subscriptionId: number) => keyNameOfCredential(credentialOfSession.get(Number(subscriptionId)));
  const paperQuery = useQuery({
    queryKey: ["open-positions", "tray", "paper"],
    queryFn: () => subscriptionApi.getOpenPositions("paper"),
    enabled: Boolean(user) && open && book === "paper",
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["open-positions", "tray"],
    queryFn: () => subscriptionApi.getOpenPositions(),
    enabled: Boolean(user),
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    // A failed poll leaves the last good book on screen. Emptying it would
    // read as "everything closed", which is the most alarming thing this
    // component could falsely say.
    placeholderData: (previous) => previous,
  });

  /*
   * Open PnL priced from the live stream, the same rule as the Open Positions
   * table and the trade page (lib/openPnl.ts). This showed the server's figure
   * polled every 30 s, so it lagged the table -- Guillaume, 2026-09-22. Each
   * session's leg keeps its share of the position.
   */
  const livePrices = useLivePrices();
  const priced = (rows: OpenPositionRow[]) => rows.map((row) => {
    const live = openPnlOf({
      // The live stream first; where it does not carry the coin (it only
      // carries the ones the price publisher watches, which is why every
      // paper row read $0 on 2026-10-01), the mark the server sent.
      markPrice: markPriceOf(livePrices?.[row.symbol]) || row.mark_price || 0,
      entry: row.entry_price || 0,
      size: row.size || 0,
      isLong: String(row.side || '').toUpperCase().startsWith('LONG'),
      fallback: row.unrealized_pnl,
    });
    return {
      ...row,
      unrealized_pnl: live,
      sessions: (row.sessions ?? []).map((s) => ({ ...s, unrealized_pnl: live * (s.share ?? 0) })),
    };
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const positions = useMemo(() => priced(data?.positions ?? []), [data, livePrices]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Only a reply that SAYS it is the paper book is shown as paper. A backend
  // older than the Paper tab ignores ?kind=paper and answers with the live
  // book; listing that here would put a paper label on a real Close button.
  const paperPositions = useMemo(
    () => priced(isPaperBook(paperQuery.data) ? paperQuery.data?.positions ?? [] : []),
    [paperQuery.data, livePrices],
  );
  const shown = book === "paper" ? paperPositions : positions;
  const shownTotal = shown.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
  /*
   * Taken as the server ordered it — newest first, by exit time, in SQL.
   * Re-sorting or re-slicing here would undo that guarantee, and taking the
   * first n of an order nobody promised is how a "recent" list quietly shows
   * the wrong rows.
   */
  const closed = useMemo(
    () => (book === "paper" ? (isPaperBook(paperQuery.data) ? paperQuery.data?.recently_closed : null) : data?.recently_closed) ?? [],
    [data, paperQuery.data, book],
  );
  /*
   * Orders Phemex is holding that have not filled. See WaitingOrder in
   * client.ts: this section exists because a resting limit order has no
   * position, so it was invisible in a tray that renders positions.
   */
  const waiting = useMemo(() => data?.waiting_at_the_broker ?? [], [data]);

  /****************
   * WHAT IS RESTING ON THE BROKER'S BOOK. M-65.
   *
   * Guillaume, 2026-09-22: "I Opened a SL manually on my broker, so there is a
   * limit order that exist and is not displayed on CopyFi, do we only monitor
   * the limit order we placed ourselves?"
   *
   * We did. This asks the exchange, so a stop set in the Phemex app appears
   * here beside the position it protects. Polled slower than the positions
   * above: it costs one venue call per open symbol, and a resting order does
   * not move.
   ****************/
  const { data: resting } = useQuery({
    queryKey: ["resting-orders", "tray"],
    queryFn: () => subscriptionApi.getRestingOrders(),
    enabled: Boolean(user) && open,
    staleTime: 15_000,
    refetchInterval: 20_000,
    placeholderData: (previous) => previous,
  });
  /* A speed run sees only what the run made (11-03, "Speed run still display my
     Open Positions"): the broker's book is read per KEY and the newcomer lens has
     no rule for it, so only the keys of the run's own sessions are kept here. */
  // While the sessions are still loading, the run's keys are not known yet: no key is kept,
  // so nothing of the account shows during a run until they are (empty is the safe side here).
  const runKeys = useMemo(() => {
    if (!mySessions) return new Set<number>();
    return new Set((mySessions as { credential_id?: number }[]).map((s) => Number(s.credential_id)).filter((id) => id > 0));
  }, [mySessions]);
  const restingOrders = useMemo(
    () => (resting?.orders ?? []).filter((o) => !inSpeedRun() || runKeys.has(Number(o.credential_id))),
    [resting, runKeys],
  );
  const unanswered = inSpeedRun() && runKeys.size === 0 ? [] : resting?.symbols_unanswered ?? [];

  /*
   * ONE ORDER, ONE ROW. Guillaume, 2026-09-29: "I have the same Limit order
   * presented twice in the Open Trades trail" -- a limit of ours resting at
   * Phemex was listed under "On the broker's book" (the exchange's own read)
   * AND under "Waiting at the broker" (our record). The book row carries our
   * order id, so the two are matched here: an order the book confirms is shown
   * there once, with how long it has waited; "Waiting" keeps only what the
   * book cannot vouch for (unconfirmed, gone, or its symbol's book unreadable).
   */
  const onTheBook = useMemo(
    () => new Map(restingOrders.filter((o) => o.our_order_id).map((o) => [o.our_order_id!, o])),
    [restingOrders],
  );
  const waitingById = useMemo(() => new Map(waiting.map((o) => [o.order_id, o])), [waiting]);
  const waitingOffTheBook = useMemo(() => waiting.filter((o) => !onTheBook.has(o.order_id)), [waiting, onTheBook]);
  // Sizes and prices are floats from the server; 11.7 must not print as 11.700000000000001.
  const num = (n: number) => (n ?? 0).toLocaleString(intlLocale(), { maximumFractionDigits: 8 });

  /*
   * Cancel one waiting order. Guillaume, 2026-09-16: "ask the broker whether
   * the order is still there, then cancel." The server does exactly that; this
   * only asks the user to confirm, shows the broker's refusal verbatim, and
   * reloads the list -- it never removes a row on its own say-so.
   */
  const cancelWaiting = async (orderId: number, symbol: string) => {
    if (!window.confirm(t("tray.confirmCancel", { symbol: an(symbol) }))) return;
    setCancellingId(orderId);
    setCancelError(null);
    try {
      await subscriptionApi.cancelOrder(orderId);
    } catch (e) {
      setCancelError({ id: orderId, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setCancellingId(null);
      // Next tick, for the same reason as ClosePositionDialog: refreshing while
      // a dialog or popover is mid-teardown leaves its overlay on the page.
      setTimeout(() => void refetch(), 0);
    }
  };
  /*
   * Cancel a strategy stop the broker booked under its own id (2026-10-14).
   * Same confirm and same refetch as above; the server re-checks the live book
   * and that the stop is this user's strategy's before anything is sent.
   */
  const cancelStrategyStop = async (o: RestingOrder) => {
    if (!window.confirm(t("tray.confirmCancel", { symbol: an(o.symbol) }))) return;
    setCancellingStop(o.order_id);
    setStopCancelError(null);
    try {
      await subscriptionApi.cancelRestingOrder(o.credential_id, o.symbol, o.order_id);
    } catch (e) {
      setStopCancelError({ id: o.order_id, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setCancellingStop(null);
      setTimeout(() => void refetch(), 0);
    }
  };
  // The sum of the live-priced rows above, not the server's 30 s figure, so the
  // header matches the rows and the Dashboard (lib/openPnl.ts).
  const total = positions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);

  /*
   * Land on the trade, not merely on its session.
   *
   * Guillaume, 2026-09-07: clicking a row "should bring you right to the Open
   * Trade linked to it and highlight it for a few seconds the exact same way
   * our Notification system do when it leads us to an order."
   *
   * Same shape as the bell's `?order=<id>`, keyed on symbol because this list
   * is the broker's book and a venue position carries no trade id of ours.
   */
  const openSession = (subscriptionId: number, symbol?: string) => {
    setOpen(false);
    navigate(focusLink(symbol ? { kind: 'open', sessionId: subscriptionId, symbol } : { kind: 'session', sessionId: subscriptionId }, 'web'));
  };

  /*
   * The closed equivalent, and deliberately the same shape.
   *
   * Guillaume, 2026-09-09: clicking a closed trade should lead to it "the exact
   * same way the open trades does" — so it lands on the session, scrolls to the
   * row and highlights it, exactly as ?open= does.
   *
   * By TRADE ID rather than symbol. That is the one real difference between the
   * two: a symbol identifies the single open position on a contract, but a
   * session can have closed forty trades on that same contract, and only one of
   * them is the row that was clicked.
   */
  // An order row leads to that order in its session, as a notification does (Guillaume 10-25).
  const openOrderRow = (subscriptionId: number, orderId: number) => {
    setOpen(false);
    navigate(focusLink({ kind: 'order', sessionId: subscriptionId, orderId }, 'web'));
  };

  const openClosedTrade = (subscriptionId: number, tradeId: string) => {
    setOpen(false);
    navigate(focusLink({ kind: 'trade', sessionId: subscriptionId, tradeId }, 'web'));
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-8 w-8 p-0"
          aria-label={
            positions.length > 0
              ? t("tray.ariaCount", { n: positions.length, amount: money(total) })
              : t("tray.title")
          }
          title={t("tray.title")}
        >
          <ChartLineUp size={20} />
          {positions.length > 0 && (
            <span
              className={`absolute top-1 end-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold
                          flex items-center justify-center text-white ${
                            total < 0 ? "bg-red-500" : "bg-emerald-500"
                          }`}
            >
              {positions.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      {/* 420, not 380. Three labelled columns need the room, and a grid
          whose labels wrap stops reading as a grid — which was the whole
          point of moving to one. */}
      {/* One scroll area under the header, capped at the space the screen has:
          four separately scrolling boxes in a popup taller than the window left
          its bottom out of reach (Guillaume, 10-21). */}
      <PopoverContent
        align="end"
        collisionPadding={8}
        className="flex w-[420px] max-h-[var(--radix-popover-content-available-height)] flex-col p-0"
      >
      <KeyNameOf.Provider value={keyNameOfSession}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="font-medium text-sm">{t("tray.title")}</div>
            <div role="group" aria-label={t("tray.bookSwitch")} className="flex rounded-md border border-border p-0.5 text-xs">
              {(["live", "paper"] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  aria-pressed={book === b}
                  onClick={() => setBook(b)}
                  className={`px-2 py-0.5 rounded ${book === b ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  {b === "live" ? t("tray.bookLive") : t("tray.bookPaper")}
                </button>
              ))}
            </div>
          </div>
          {shown.length > 0 && (
            <div className={`text-sm font-semibold ${toneFor(shownTotal)}`}>
              {money(shownTotal)}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <TraySection id="open" title={t("tray.currentlyOpen")} count={shown.length}>
          {(book === "paper" ? paperQuery.isLoading && !paperQuery.data : isLoading && !data) ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("tray.loading")}
            </div>
          ) : shown.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {book === "paper" ? t("tray.emptyPaper") : t("tray.empty")}
            </div>
          ) : (
            shown.map((row) => (
              <Position
                // Paper lists each session's position on its own row, so the
                // session is part of the key; live has one row per coin and side.
                key={`${row.symbol}|${row.side}|${book === "paper" ? row.sessions?.[0]?.subscription_id ?? "" : ""}`}
                row={row}
                onOpenSession={openSession}
                onCloseRequested={setClosingRow}
              />
            ))
          )}
        </TraySection>

        {/****************
          * RECENTLY CLOSED, UNDER WHAT IS STILL OPEN.
          *
          * Guillaume, 2026-09-08: "add the history to the tray too."
          *
          * Account-wide, unlike the session page's version, because the tray
          * is account-wide — so each row names the session it belonged to.
          * Without that, two sessions running turns this into a list of
          * trades with no owner.
          *
          * Shown even when nothing is open. A tray that says "nothing open
          * right now" and stops is a dead end; what closed a minute ago is
          * exactly what somebody is looking for at that moment.
          ****************/}
        {/****************
          * WAITING AT THE BROKER.
          *
          * Above Recently closed, because this is the section that can still
          * cost money: an order sitting on Phemex's book will fill, and if the
          * trader has already left the position it opens one nobody wants.
          * Guillaume had to find his on Phemex and cancel it by hand.
          *
          * The hours are shown plainly rather than styled as an alarm. A limit
          * order waiting is ORDINARY — it is what a limit order does — and
          * colouring it red would teach people to ignore the colour.
          ****************/}
        {/****************
          * ON THE BROKER'S BOOK.
          *
          * Separate from "Waiting at the broker" above, which is OUR unfilled
          * orders. This section is the exchange's own answer, so it includes
          * the stops and take profits somebody set in the Phemex app -- the
          * orders that decide where a position ends and that CopyFi could not
          * see at all before M-65.
          *
          * A symbol the exchange would not answer for is named. "No stop
          * showing" and "we could not ask" are opposite facts about a
          * position, and only one of them is safe to act on.
          ****************/}
        {book === "live" && (restingOrders.length > 0 || unanswered.length > 0) && (
          <TraySection id="book" title={t("tray.brokerBook")} count={restingOrders.length}>
            <div>
              {/* LIKE A BROKER'S OPEN ORDERS (Guillaume, 2026-10-11: "what is the
                  size, what is the price... make it like on a broker"): the order
                  type and side on the first line, then Price, Size, Value and how
                  long it has waited, each under its own label. */}
              {restingOrders.map((o) => {
                const ours = o.our_order_id ? waitingById.get(o.our_order_id) : undefined;
                const at = o.trigger_price || o.price || 0;
                const value = at * (o.quantity || 0);
                return (
                <div key={`${o.order_id}|${o.cl_ord_id}`}
                  className={`w-full px-4 py-2 text-start hover:bg-muted/50 transition-colors ${ours ? "cursor-pointer" : ""}`}
                  onClick={ours ? () => openOrderRow(ours.subscription_id, ours.order_id) : undefined}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">
                      <SymbolLabel symbol={o.symbol} />
                      <span className={`ms-1.5 text-xs font-semibold ${bookSide(o) === 'buy' ? 'text-success' : 'text-danger'}`}>
                        {t(`tray.book.${bookType(o)}`)} {t(`tray.book.${bookSide(o)}`)}
                      </span>
                      <span className="ms-1.5 text-xs text-muted-foreground">
                        {o.kind === 'stop_loss'
                          ? t('tray.kind.stopLoss')
                          : o.kind === 'take_profit'
                          ? t('tray.kind.takeProfit')
                          : o.kind === 'protective'
                          ? t('tray.kind.protective')
                          : t('tray.kind.entry')}
                      </span>
                    </span>
                    {o.our_order_id ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        disabled={cancellingId === o.our_order_id}
                        onClick={(e) => { e.stopPropagation(); void cancelWaiting(o.our_order_id!, o.symbol); }}
                      >
                        {cancellingId === o.our_order_id ? t("tray.cancelling") : t("tray.cancel")}
                      </Button>
                    ) : o.strategy_stop ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px]"
                        disabled={cancellingStop === o.order_id}
                        onClick={(e) => { e.stopPropagation(); void cancelStrategyStop(o); }}
                      >
                        {cancellingStop === o.order_id ? t("tray.cancelling") : t("tray.cancel")}
                      </Button>
                    ) : null}
                  </div>
                  <div className="mt-1 grid grid-cols-4 gap-2 text-[11px]">
                    <BookCell label={o.trigger_price ? t('tray.book.trigger') : t('tray.book.price')}
                      value={at ? px4(at) : '—'} />
                    <BookCell label={t('tray.book.size')} value={num(o.quantity)} />
                    <BookCell label={t('tray.book.value')} value={value > 0 ? fmtUsd(value, 2) : '—'} />
                    <BookCell label={t('tray.book.age')}
                      value={ours && Number.isFinite(ours.waiting_hours) ? t('tray.book.ageHours', { hours: fmtFixed(ours.waiting_hours, 1) }) : '—'} />
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {o.strategy_stop
                      ? t('tray.placedByStrategy')
                      : o.placed_by === 'copyfi' ? t('tray.placedByCopyfi') : t('tray.placedByYou')}
                    {keyNameOfCredential(o.credential_id) ? ` · ${keyNameOfCredential(o.credential_id)}` : ""}
                  </div>
                  {cancelErrorFor(cancelError, o.our_order_id) && (
                    <span className="text-[10px] text-destructive">{cancelErrorFor(cancelError, o.our_order_id)}</span>
                  )}
                  {o.strategy_stop && stopCancelError?.id === o.order_id && (
                    <span className="text-[10px] text-destructive">{stopCancelError.message}</span>
                  )}
                </div>
                );
              })}
              {unanswered.length > 0 && (
                <div className="px-4 py-2 text-[10px] text-muted-foreground">
                  {t('tray.unanswered', { symbols: unanswered.map((s) => an(s)).join(', ') })}
                </div>
              )}
            </div>
          </TraySection>
        )}

        {book === "live" && waitingOffTheBook.length > 0 && (
          <TraySection id="waiting" title={t("tray.waiting")} count={waitingOffTheBook.length}>
            <div>
              {waitingOffTheBook.map((o) => (
                <div key={o.order_id} className="w-full px-4 py-2 text-start hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => openOrderRow(o.subscription_id, o.order_id)}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-foreground">
                      <SymbolLabel symbol={o.symbol} />
                      {o.direction ? (
                        <span className="ms-1.5 text-xs text-muted-foreground">{o.direction}</span>
                      ) : null}
                    </span>
                    {/****************
                      * "WORKING" ONLY IF THE BROKER SAYS SO.
                      *
                      * This word was printed for any order we had RECORDED as
                      * sent. Nothing ever took one off the list, so an order the
                      * venue had cancelled sat here saying "Working" forever.
                      *
                      * Guillaume, 2026-09-17: "CopyFi say we have a limit order
                      * on XRP waiting, but the broker does not have."
                      *
                      * the_broker_still_has_it carries the venue's own answer in
                      * three states. "unknown" is NOT "yes" -- we have no record
                      * from the broker either way, and saying Working there is
                      * the same guess that caused this.
                      ****************/}
                    {(() => {
                      const v = String((o as any).the_broker_still_has_it ?? "");
                      if (v === "yes") {
                        return <span className="text-xs text-primary">{t("tray.working")}</span>;
                      }
                      if (v.startsWith("no")) {
                        return (
                          <span className="text-xs text-destructive">
                            {t("tray.brokerGone")}
                          </span>
                        );
                      }
                      return (
                        <span className="text-xs text-muted-foreground">
                          {t("tray.unconfirmed")}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                    {o.action ? `${o.action} · ` : ""}
                    {num(o.size)}
                    {o.price > 0 ? ` @ ${num(o.price)}` : ""}
                    {Number.isFinite(o.waiting_hours) && o.waiting_hours >= 0.1
                      ? t("tray.waitingHours", { hours: fmtFixed(o.waiting_hours, 1) })
                      : null}
                    {String((o as any).the_broker_still_has_it ?? "").startsWith("no") && (
                      <span className="text-destructive">
                        {" · "}
                        {String((o as any).the_broker_still_has_it)}
                      </span>
                    )}
                    {keyNameOfSession(o.subscription_id) ? ` · ${keyNameOfSession(o.subscription_id)}` : ""}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      disabled={cancellingId === o.order_id}
                      onClick={(e) => { e.stopPropagation(); void cancelWaiting(o.order_id, o.symbol); }}
                    >
                      {cancellingId === o.order_id ? t("tray.cancelling") : t("tray.cancelOrder")}
                    </Button>
                    {cancelErrorFor(cancelError, o.order_id) && (
                      <span className="text-xs text-destructive">{cancelErrorFor(cancelError, o.order_id)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TraySection>
        )}

        {(closed?.length ?? 0) > 0 && (
          <TraySection id="closed" title={t("tray.recentlyClosed")} count={closed?.length ?? 0}>
            <div>
              {closed!.map((t) => (
                <button
                  key={t.trade_id}
                  type="button"
                  onClick={() => openClosedTrade(t.subscription_id, t.trade_id)}
                  className="w-full px-4 py-2 text-start hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">
                        <SymbolLabel symbol={t.symbol} />
                        <span className="text-muted-foreground font-normal">
                          {" "}
                          {t.direction} · {t.leverage}x
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {closedPrices(t.entry_price, t.exit_price)}
                        {/* The session number, because the trader's name does
                            not identify one: two sessions can follow the same
                            trader, and on this account-wide list that is the
                            difference between knowing which session made this
                            and guessing. */}
                        {` · #${t.subscription_id}`}
                        {t.trader_name ? ` ${t.trader_name}` : ""}
                        {keyNameOfSession(t.subscription_id) ? ` · ${keyNameOfSession(t.subscription_id)}` : ""}
                      </div>
                    </div>
                    {(() => {
                      /* Net, after trading fees and funding: the basis every
                         total in the app is on since 40ad6d4 (Guillaume,
                         2026-09-10). realized_pnl stays the venue's gross
                         figure, so the costs come off here, once, and the gross
                         is kept underneath when they change the answer. */
                      const net = t.realized_pnl - (t.total_fees || 0) - (t.funding_paid || 0);
                      return (
                        <div className={`shrink-0 text-end ${toneFor(net)}`}>
                          <div className="text-xs font-medium tabular-nums">{money(net)}</div>
                          {Math.abs(net - t.realized_pnl) >= 0.005 && (
                            <div className="text-[9px] text-muted-foreground tabular-nums">
                              {tr("tray.beforeFees", { amount: money(t.realized_pnl) })}
                            </div>
                          )}
                          {/* When it closed (Guillaume, 2026-10-01). In this
                              column because it never truncates; the line on the
                              left does. */}
                          {closedAt(t.exit_time) && (
                            <div
                              className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap"
                              title={closedAtTitle(t.exit_time)}
                            >
                              {closedAt(t.exit_time)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </button>
              ))}
            </div>
          </TraySection>
        )}
        </div>

        {/* Says where the numbers come from. The dashboard and this tray read
            the same venue book, and saying so is how a user knows a difference
            from their exchange app is worth reporting. */}
        {shown.length > 0 && (
          <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground">
            {book === "paper" ? t("tray.paperNote") : t("tray.liveFromExchange")}
          </div>
        )}
      </KeyNameOf.Provider>
      </PopoverContent>
    </Popover>

    {/* Outside the Popover on purpose: the tray closes when focus moves to the dialog,
        and a dialog nested inside it would be torn down mid-question -- taking the
        answer, and on a confirmed close the report of what actually happened, with it. */}
    <ClosePositionDialog
      row={closingRow}
      paper={book === "paper"}
      onClose={() => setClosingRow(null)}
      onClosed={() => void (book === "paper" ? paperQuery.refetch() : refetch())}
    />
    </>
  );
}

export default OpenTradesTray;

/** The broker's order type in three words a trader reads: limit, market or stop. */
function bookType(o: { order_type: string; trigger_price: number }): 'limit' | 'market' | 'stop' {
  const ty = (o.order_type || '').toLowerCase();
  if (o.trigger_price || ty.includes('stop') || ty.includes('touched')) return 'stop';
  if (ty.includes('market')) return 'market';
  return 'limit';
}

/** Buy or sell, as the exchange records the order. */
function bookSide(o: { side: string }): 'buy' | 'sell' {
  return (o.side || '').toLowerCase().startsWith('s') ? 'sell' : 'buy';
}

function BookCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-mono tabular-nums text-foreground">{value}</div>
    </div>
  );
}

/** A price in the tray: at most 4 decimals (Guillaume 11-04: "too much decimal"). */
function px4(v: number): string {
  return Number(v).toLocaleString(intlLocale(), { maximumFractionDigits: 4 });
}
